import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, retry, switchMap } from 'rxjs/operators';
import { MenuItem } from '../interfaces/menu-item';
import { MenuResponse } from '../interfaces/menu-response';
import { environment } from '../../environments/environment';
import { AuthenticationOidcService } from './authentication-oidc-service';
import { getCurrentProviderType } from '../utils/provider-type-information-utils';
import { FromProviderType } from '../interfaces/from-type-enum';

@Injectable({ providedIn: 'root' })
export class MenuService {
  private readonly apiMenuPath = `/menu`;

  constructor(
    private http: HttpClient,
    private authenticationOidcService: AuthenticationOidcService,
  ) {}

  /**
   * Fetches the menu items for a given user email, sending the user's token in the Authorization header.
   * @param email User email
   * @returns Observable emitting MenuResponse with items or error
   */
  getMenu(email: string): Observable<MenuResponse> {
    if (!email) {
      return of({ items: [], error: 'Email is required.' });
    }

    var providerType = getCurrentProviderType();
    if (!providerType) {
      return of({ items: [], error: 'Provider type is not set.' });
    }

    const apiUrl = this.getApiUrl(providerType);

    const url = `${apiUrl}?email=${encodeURIComponent(email)}`;
    return this.authenticationOidcService.getBearerToken().pipe(
      switchMap((token) => {
        const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
        return this.http.get<MenuItem[]>(url, { headers }).pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(retryCount * 1000),
          }),
          map((items) => ({ items })),
          catchError((error) => this.handleError(error)),
        );
      }),
    );
  }

  /**
   * Handles HTTP errors and maps them to MenuResponse
   * @param error HttpErrorResponse or unknown error
   * @returns Observable<MenuResponse> with error message
   */
  private handleError(error: unknown): Observable<MenuResponse> {
    let message = 'Unknown error';
    if (error instanceof HttpErrorResponse) {
      if (error.error instanceof ErrorEvent) {
        message = error.error.message;
      } else if (error.status) {
        message = `API error: ${error.status} ${error.statusText}`;
      } else if (typeof error.message === 'string') {
        message = error.message;
      }
    } else if (typeof error === 'string') {
      message = error;
    } else if (error && typeof (error as any).message === 'string') {
      message = (error as any).message;
    }
    return of({ items: [], error: message });
  }

  /**
   * Returns the API URL for the given provider type.
   * @param providerType The provider type
   * @returns The API URL as a string
   */
  private getApiUrl(providerType: FromProviderType): string {
    switch (providerType) {
      case FromProviderType.IMPERIA:
        return environment.API_IMPERIA_URL + this.apiMenuPath;
      case FromProviderType.SUITEEDGE:
        return environment.API_SUITEEDGE_URL + this.apiMenuPath;
      default:
        throw new Error('Unsupported provider type');
    }
  }
}
